<template>
	<Application class="ContractSigningView" :errorDetail="errorDetail" v-model:errorTitle="errorTitle" :successDetail="successDetail"
		v-model:successTitle="successTitle" :loading="loading"
		:style="{ 'padding-left': isSideBarFixed && !isMobile ? '315px' : '15px' }"
	>
		<!-- TODO: we should not use bootstrap classes -->
		<template #navbar>
			<v-spacer></v-spacer>
			<ButtonList
				:disabled="!selectedContract?.contractTemplate?.name || !selectedContract?.signedDate"
				:buttonText="$t('text.download')"
				:options="downloadLocaleOptions"
				prependIcon="mdi-download"
				@option-click="handleDownload"
				:loading="pdfExportLoading"
			>
				<template #prepend="{ option }">
					<LanguageFlag :modelValue="option.key" style="margin-right: 5px" />
				</template>
			</ButtonList>
		</template>

		<template v-if="tokenIsValid">
			<SideBar class="signing-sidebar"
				:preselectedActiveLink="'contracts'"
				:class="{ mobileSidebar: isMobile }"
				:translations="translations"
				:checkTranslationStatus="false"
				@action="handleAction($event)"
			>
				<template #customActions>
					<div class="pa-4 flex-column d-flex">
						<ContractCard v-for="contract in contracts" :key="contract.id" :contract="contract"
							:client-name="clientNames[contract.contractTemplate?.contractSequence?.clientId]"
							:interactive="true"
							class="mb-4" @click="setActiveLink(contract.id)" />
					</div>
				</template>

				<template #customNavigation>
					<div class="pb-4 flex-column d-flex signing-sidebar-navigation">
						<SideBarNavigation v-for="navigation in navigations" :key="navigation.clientId"
							:model-value="selectedContract?.id" :title="navigation.title"
							:sections="navigation.contracts" :expanded="true"
							:title-error-icon="hasUnsignedContracts(navigation.clientId) ? 'mdi-alert' : null"
							:preselected-active-link="selectedContract?.id" @setActive="setActiveLink($event)" />
						<div class="bottom-navigation">
							<div :class="{ active: viewTerminatedContracts }" @click="setViewTerminatedContracts">
								<span>{{ $t('text.terminatedContracts') }}</span>
							</div>
						</div>
					</div>
				</template>
			</SideBar>

			<div style="margin-top: 60px;" v-if="viewTerminatedContracts">
				<Disclosure :title="$t('text.terminatedContracts')" expanded class="terminated-disclosure">
					<FieldSet :label="$t('text.terminatedContracts')" :info-text="$t('text.terminatedContractsInfoText')">
						<ContractsList :contracts="terminatedContracts" :contractors="contractors" :terminated="true" :client-names="clientNames" />
					</FieldSet>
				</Disclosure>
			</div>
			<div style="margin-top: 60px;" v-else-if="selectedContract">
				<Disclosure expanded class="signing-disclosure">
					<template #header-accessory>
						<div class="header-accessory">
							<v-icon size="24px">mdi-gavel</v-icon>
							<div>
								<span>
									{{ selectedContract.contractTemplate.name[serviceLocale] || getContractName(selectedContract) }}
								</span>
								<span>
									{{ $t('text.issuedOn') }} {{ issuedOnText }}
								</span>
							</div>
							<div>
								<div class="d-flex flex-column">
									<div class="d-flex">
										<v-spacer></v-spacer>
										<div :style="{ 'background-color': backgroundColorChip }" class="status-chip">
											<v-icon size="18px">mdi-draw</v-icon>
											<span>{{ statusText }}</span>
										</div>
									</div>
									<div v-if="shouldShowSignedInfo" class="d-flex">
										<v-spacer></v-spacer>
										<span class="info-text" :style="{ 'color': textColor }" v-html="infoText" />
									</div>
								</div>
							</div>
						</div>
					</template>
					<div ref="contractInformation" class="contract-information">
						<template v-if="isPhysical">
							<FieldSet class="physical-title" :label="$t('text.physicalContractInfo')">
								<span class="physical-text">{{ $t('text.physicalContractInfoText') }}</span>
							</FieldSet>
							<FieldSet class="mt-8" :label="$t('text.yourData')"
								:info-text="$t('text.yourDataInfoText')" />
							<FieldSet class="mt-8">
								<div class="d-flex">
									<v-icon class="mr-4" size="24px">mdi-information</v-icon>
									<span class="physical-text">{{ $t('text.noOnlineDataAvailable') }}</span>
								</div>
							</FieldSet>
						</template>
						<template v-else>
							<v-alert v-if="isViewingNonSignedLocale" type="info" variant="tonal" class="mb-4">
								{{ $t('text.viewingTranslationNotice') }}
							</v-alert>
							<ContractDetail :show-contractor="true" :contract="selectedContract" :contractor="contractorForContract"
								:pdf-export-loading="pdfExportLoading" @openDialog="openDialog()" :readOnly="isReadonly"
							/>
							<template v-if="toBeSigned && !isReadonly">
								<FieldSet class="mt-8">
									<div class="d-flex flex-wrap" style="gap: 24px;">
										<!-- TODO: we should not override title -->
										<!-- TODO: why is width: 100% necessary here? -->
										<Field :title="`${$t('text.signatureName')} (${$t('text.required')})`"
											class="flex-grow-1" typeName="Contract" fieldName="signatureName"
											v-model="signatureName" dataCy="signatureName"
										/>
										<!-- TODO: we should not override title -->
										<!-- TODO: why is width: 100% necessary here? -->
										<Field :title="`${$t('text.signaturePosition')} (${$t('text.required')})`"
											class="flex-grow-1" typeName="Contract" fieldName="signaturePosition"
											v-model="signaturePosition" dataCy="signaturePosition"
										/>
									</div>
									<v-checkbox id="confirmCheck" hide-details :label="$t('text.contractConfirmToS')" v-model="confirmCheck" color="blue" />
									<v-checkbox id="downloadCheck" hide-details :label="$t('text.downloadContract')" v-model="downloadCheck" color="blue" />
								</FieldSet>
								<div v-if="!pdfExportLoading" class="d-flex mt-8">
									<v-spacer></v-spacer>
									<v-btn class="greenButton" theme="dark" elevation="0" data-cy="confirm"
										@click="signContract" :disabled="disableConfirmButton"
									>
										{{ $t('text.signContract') }}
									</v-btn>
								</div>
							</template>
						</template>
						<div v-if="(isPhysical || !toBeSigned) && shouldShowSignedInfo" class="d-flex mt-8">
							<ContractCard :contract="selectedContract"
								:style="{
									flex: !pdfExportLoading ? 0.4 : 1,
									minHeight: '130px',
									width: isMobile ? '100%' : 'auto',
								}"
							/>
						</div>
					</div>
				</Disclosure>
			</div>

			<DialogV2 ref="contractorDialog"
				:title="$t('text.editContractorInformation')"
				:onConfirm="updateContractor"
				:disableConfirmButton="hasFormError"
				:height="isMobile ? 'calc(100vh - 140px)' : 'calc(100vh - 5px)'"
			>
				<template #content>
					<ContractorForm ref="contractorForm"
						v-model="contractorClone"
						@update:modelValue="contractorClone = $event"
						@update:hasFormError="hasFormError = $event"
						:isTourOperator="isTourOperator"
					/>
				</template>
			</DialogV2>
			<IfFeatureFlag flag="FEATURE_CONTRACTOR_CHANGES_WIZARD">
			<EntityHistory
				v-if="contractorForContract && !token"
				:entity-type="EntityType.CONTRACTOR"
				:entity-id="contractorForContract.id"
				:executive="contractExecutive"
				changes-method-name="getContractorChangesSince"
				recent-changes-method-name="getContractorRecentChanges"
				page-visit-method-name="logContractorPageVisit"
			/>
			</IfFeatureFlag>
		</template>
	</Application>
</template>

<script>
import Common from '../../../mixins/Common.vue'
import Application from '../Application.vue'
import ContractExecutive from '../../../../../api2/src/modules/contract/ContractExecutive'
import DialogV2 from '../../../components/common/DialogV2.vue'
import ContractorForm from '../../../components/contract/ContractorForm.vue'
import ContractCard from '../../../components/contract/ContractCard.vue'
import SideBar from '../../../components/common/SideBar.vue'
import SideBarNavigation from '../../../components/common/SideBarNavigation.vue'
import Disclosure from '../../../components/common/Disclosure.vue'
import FieldSet from '../packageDesigner/FieldSet.vue'
import Field from '../../../components/fields/Field.vue'
import moment from 'moment'
import Loading from '../../../mixins/Loading.vue'
import ButtonList from '../../../components/common/ButtonList.vue'
import ContractDetail from '../../../components/contract/ContractDetail.vue'
import ContractsList from '../../../components/contract/ContractsList.vue'
import EntityHistory from '../../../components/shared/EntityHistory.vue'
import { EntityType } from '../../../../../api2/src/modules/audit/audit.types'
import IfFeatureFlag from '@/components/ifFeatureFlag/IfFeatureFlag.vue'
import LanguageFlag from '../../../components/common/LanguageFlag.vue'
import TourOperatorExecutive from '../../../../../api2/src/modules/trade-hub/TourOperatorExecutive'
import { ContractSequenceType } from '../../../../../shared/types/account-types'
import { NotFoundError } from '../../../../../api2/src/framework/rmi-errors'
import Toast from '@/mixins/Toast.vue'

export default {
	components: { Application, DialogV2, ContractorForm, SideBar, ContractCard, SideBarNavigation, Disclosure, FieldSet, Field, ButtonList, ContractDetail, ContractsList, EntityHistory, IfFeatureFlag, LanguageFlag },
	mixins: [ Common, Loading, Toast ],
	props: {
		token: { type: String, required: true },
	},
	data() {
		return {
			tokenIsValid: false,
			contractors: [],
			contractsByContractor: {},
			contracts: [],
			loading: false,
			clientNames: {},
			hasFormError: false,
			selectedContract: null,
			contractorForContract: null,
			contractorClone: null,
			confirmCheck: false,
			downloadCheck: false,
			pdfExportLoading: false,
			signatureName: '',
			signaturePosition: '',
			windowWidth: window.innerWidth,
			viewTerminatedContracts: false,
			terminatedContracts: [],
			isReadonly: false,
			preview: '',
			EntityType, // Make the enum available in the template
			contractExecutive: new ContractExecutive(this)
		}
	},
	computed: {
		isMobile() {
			return this.windowWidth < 768
		},
		navigations() {
			const grouped = []

			this.contracts?.forEach(item => {
				const clientId = item.contractTemplate.contractSequence.clientId
				const id = item.id
				const title = this.getContractName(item)

				let group = grouped.find(g => g.clientId === clientId)

				if (!group) {
					group = { clientId, title: this.clientNames[clientId], contracts: [] }
					grouped.push(group)
				}

				group.contracts.push({
					id, title,
					state: item.signedDate || !item.contractTemplate.needsSignature ? 'valid' : 'error',
					errorIconColor: 'black',
					validIcon: true,
					validIconColor: 'black',
				})
			})
			return grouped
		},
		signedContracts() {
			return this.contracts?.filter(x => !!x.signedDate)
		},
		isPhysical() {
			return !this.selectedContract?.contractTemplate?.needsSignature
		},
		isSigned() {
			return !!this.selectedContract?.signedDate
		},
		toBeSigned() {
			// TODO: in Phase 1 & 2 we allow overdue contracts to be signed. In a future phase we might have to check if the due date is set and if it is, check if it is in the past to prevent signing overdue contracts.
			return !this.isSigned
		},
		overdue() {
			return !this.isSigned && this.selectedContract?.dueDate && moment().isAfter(moment(this.selectedContract?.dueDate))
		},
		backgroundColorChip() {
			if (this.isSigned || this.isPhysical) return '#64C823'
			if (this.overdue) return '#F34545'
			if (this.toBeSigned) return '#FFB400'
			return '#8F8F8F'
		},
		statusText() {
			if (this.isSigned || this.isPhysical) return this.$t('text.signed')
			return this.$t('text.unsigned')
		},
		infoText() {
			if (this.isPhysical) return ''
			if (this.isSigned) {
				const signedDate = moment(this.selectedContract?.signedDate).format('DD.MM.YYYY')
				const signedTime = moment(this.selectedContract?.signedDate).format('HH:mm:ss')
				const signedBy = this.selectedContract?.signatureName
				const signedByPosition = this.selectedContract?.signaturePosition
				return `${ this.$t('text.signedOn') } <b>${ signedDate } ${ this.$t('text.at') } ${ signedTime }</b> ${this.$t('text.by')} <b>${ signedBy }, ${ signedByPosition }</b>`
			}
			if (this.toBeSigned) {
				const dueDate = moment(this.selectedContract?.dueDate).format('DD.MM.YYYY')
				return `<b>${ this.$t('text.signUntil') } ${ dueDate }</b>`
			}
			if (this.overdue) {
				return `<b>${ this.$t('text.notSignedInTime') }</b>`
			}
			return ''
		},
		issuedOnText() {
			const createdDate = moment(this.selectedContract?.createDate).format('DD.MM.YYYY')
			const createdTime = moment(this.selectedContract?.createDate).format('HH:mm:ss')
			return `${ this.$t('text.issuedOn') } ${ createdDate } ${this.$t('text.at')} ${ createdTime }`
		},
		textColor() {
			if (this.isSigned) return '#28500E'
			if (this.toBeSigned) return '#664800'
			if (this.overdue) return '#611C1C'
			return '#DBDBDB'
		},
		disableConfirmButton() {
			return this.hasFormError || !this.signatureName || !this.signaturePosition || !this.confirmCheck
		},
		loggedInEmail () {
			if (!this.userIsServiceProviderOrImpersonating) {
				return null
			}
			const serviceProvider = this.$store.state.isImpersonation ? this.$store.state.selectedServiceProvider : this.$store.state.loggedInUser?.fields?.serviceProvider?.de
			return serviceProvider?.fields?.userAccount?.de?.fields?.email?.de
		},
		translations() {
			if (!this.selectedContract?.text) return []
			return Object.keys(this.selectedContract.text).filter(k => !!k && this.selectedContract.text[k] !== '').map(k => ({
				locale: { code: k, name: this.$store.state.locales.find(l => l.code === k)?.name || k, isSigned: this.selectedContract?.signedLocale === k, signedDate: this.selectedContract?.signedLocale === k ? this.selectedContract?.signedDate : null }
			}))
		},
		isTourOperator() {
			return this.contracts.some(x => x.contractTemplate.contractSequence.type === ContractSequenceType.TOUR_OPERATOR)
		},
		shouldShowSignedInfo() {
			if (!this.selectedContract?.signedDate) return true
			if (!this.selectedContract.signedLocale) return true
			return this.serviceLocale === this.selectedContract.signedLocale
		},
		isViewingNonSignedLocale() {
			return this.selectedContract?.signedDate && this.selectedContract?.signedLocale && this.serviceLocale !== this.selectedContract.signedLocale
		},
		downloadLocaleOptions() {
			const name = this.selectedContract?.contractTemplate?.name
			if (!name) return []
			if (this.selectedContract?.signedLocale && this.selectedContract?.signedDate) {
				const signedLocale = this.selectedContract.signedLocale
				if (name[signedLocale]) {
					return [{ key: signedLocale, title: name[signedLocale] }]
				}
			}
			return Object.keys(name).filter(locale => name[locale] !== '').map(locale => ({ key: locale, title: name[locale] }))
		},
	},
	watch: {
		'selectedContract.id'() {
			this.downloadCheck = false
			this.confirmCheck = false
		}
	},
	async mounted() {
		window.addEventListener('resize', this.onResize)
		await this.getContractorAndContracts()
		console.log('Contract is of type Tour Operator:', this.selectedContract?.isTourOperatorContract)
	},
	beforeUnmount() {
		window.removeEventListener('resize', this.onResize)
	},
	methods: {
		async downloadContractServer(contract, locale = this.serviceLocale) {
			this.pdfExportLoading = true
			const tempContract = { ...this.selectedContract }
			this.selectedContract = contract
			try {
				const contractExecutive = new ContractExecutive(this)
				const response = this.token ? await contractExecutive.downloadContractByToken(contract.id, this.token, locale) : await contractExecutive.downloadContract(contract.id, locale)
				saveAs(new Blob([response]), this.getPdfName(contract, locale))
			}
			catch (error) {
				this.showError(error.message)
			}
			finally {
				this.contractorForContract = this.findContractOwner(tempContract.id)
				this.selectedContract = tempContract
				this.pdfExportLoading = false
			}
		},
		getPdfName(contract, locale) {
			const clientName = this.clientNames[contract.contractTemplate?.contractSequence?.clientId]
			const contractName = contract.contractTemplate.name[locale]
			const contractSignDate = contract.signedDate
			const sanitizedClientName = clientName.replace(/[^\w\s]/gi, '')
			const sanitizedContractName = contractName.replace(/[^\w\s]/gi, '')
			const dateString = moment(contractSignDate).format('DD.MM.YYYY HH-mm')
			return `${sanitizedClientName} ${sanitizedContractName} SIGNED ${dateString}.pdf`
		},
		hasUnsignedContracts(clientId) {
			return this.navigations.find(x => x.clientId === clientId)?.contracts?.some(x => x.state === 'error')
		},
		onResize() {
			this.windowWidth = window.innerWidth
		},
		openDialog() {
			if (this.isReadonly) return

			// Use first contractor's data for initial form
			if (!this.contractorClone) {
				this.contractorClone = JSON.parse(JSON.stringify(this.contractors[0]))
			}
			else {
				this.contractorClone = JSON.parse(JSON.stringify(this.contractorForContract))
			}
			if (this.$refs.contractorDialog) {
				this.$refs.contractorDialog.open()
			}
		},
		async getContractsAndContractors() {
			let contractExecutive = new ContractExecutive(this)
			let result
			if (this.token) {
				try {
					result = await contractExecutive.getContractsAndContractorsByToken(this.token)
				}
				catch (error) {
					if (error instanceof NotFoundError) {
						this.showErrorToast(error.message)
						return
					}
					this.showErrorToast(this.$t('text.errorLoadingContracts'), true)
					return
				}
			}
			else {
				if (!this.loggedInEmail) {
					this.showError('Not Authorized!')
					// setTimeout(() => {
					// 	this.$emit('show-login')
					// }, 1000)
					return
				}
				const providerId = this.$store.state.selectedServiceProvider.sys.id
				try {
					result = await contractExecutive.getContractsAndContractorsByProviderId(providerId)
				}
				catch (error) {
					if (error instanceof NotFoundError) {
						this.showErrorToast(error.message)
						return
					}
					this.showErrorToast(this.$t('text.errorLoadingContracts'), true)
					return
				}
			}

			this.contractors = result.contractors
			this.contractsByContractor = result.contractsByContractor
			this.isReadonly = result.isReadonly
			// filter non terminated
			const allContracts = Object.values(result.contractsByContractor).flat()
			this.terminatedContracts = allContracts.filter(contract => contract.status === 'TERMINATED')
			this.contracts = allContracts.filter(contract => !contract.endDate || moment(contract.endDate).isSameOrAfter(moment()))
			this.clientNames = result.clientNames
		},
		async getContractorAndContracts() {
			this.loading = true
			try {
				await this.getContractsAndContractors()
				this.tokenIsValid = true

				if (this.contracts.length) {
					this.setActiveLink(this.contracts[0].id)
				}
				
				this.$nextTick(() => {
					if (!this.isReadonly) {
						this.openDialog()
					}
				})
			}
			catch (error) {
				this.showError(error.message)
				setTimeout(() => {
					this.$emit('show-login')
				}, 5000)
			}
			this.loading = false
		},
		async updateContractor() {
			if (this.isReadonly) return

			this.loading = true

			try {
				// Update pending logo changes first if there are one
				// If it fails here, we do not continue to update the contractor data
				if (this.$refs.contractorForm) {
					await this.$refs.contractorForm.processLogoUpdates()
				}
			} catch (error) {
				let errorMessage = this.$t('text.updateContractorLogoError')
				if (error?.message?.includes('Invalid user credentials')) {
					errorMessage = this.$t('text.updateContractorErrorInvalidPeakUserCredentials')
				}
				this.showError(errorMessage)
				this.loading = false
				return
			}

			try {
				let contractExecutive = new ContractExecutive(this)
				
				// Update all contractors with same data
				if (this.token) {
					await contractExecutive.bulkUpdateContractorsWithToken([{
						...this.contractorForContract,
						email: this.contractorClone.email,
						logoUrl: this.contractorClone.logoUrl,
						businessProfileData: this.contractorClone.businessProfileData
					}], this.token)
				}
				else {
					await contractExecutive.bulkUpdateContractors([{
						...this.contractorForContract,
						email: this.contractorClone.email,
						logoUrl: this.contractorClone.logoUrl,
						businessProfileData: this.contractorClone.businessProfileData
					}])
				}

				// Refresh data after update
				await this.getContractsAndContractors()

				this.successTitle = this.$t('text.SUCCESS')
				this.successDetail = this.$t('text.contractorUpdatedDetail')
				
				// Find the contractor that owns the selected contract
				const contractOwner = this.findContractOwner(this.selectedContract.id)
				this.contractorForContract = contractOwner
				this.contractorClone = JSON.parse(JSON.stringify(contractOwner))
				
				this.setActiveLink(this.selectedContract.id)
			}
			catch (error) {
				this.showError(error.message)
			}
			this.loading = false
		},
		findContractOwner(contractId) {
			for (const [contractorId, contracts] of Object.entries(this.contractsByContractor)) {
				if (contracts.some(c => c.id === contractId)) {
					return this.contractors.find(c => c.id === contractorId)
				}
			}
			return null
		},
		async signContract() {
			if (this.isReadonly) return

			await this.loadingOverlay(async () => {
				try {
					let contractExecutive = new ContractExecutive(this)
					const contractorId = this.findContractOwner(this.selectedContract.id)?.id
					const clientId = this.$store.state?.selectedClient?.sys?.id ?? this.selectedContract.contractTemplate.contractSequence.clientId
					
					if (this.token) {
						await contractExecutive.signContractsWithToken(clientId, contractorId, [this.selectedContract.id], this.signatureName, this.signaturePosition, this.token, this.serviceLocale)
					}
					else {
						await contractExecutive.signContracts(clientId, contractorId, [this.selectedContract.id], this.signatureName, this.signaturePosition, this.serviceLocale)
					}
					await this.getContractsAndContractors()
	
					// Find the contractor that owns the selected contract
					const contractOwner = this.findContractOwner(this.selectedContract.id)
					this.contractorForContract = contractOwner
					this.contractorClone = JSON.parse(JSON.stringify(contractOwner))
	
					this.setActiveLink(this.selectedContract.id)
	
					if (this.downloadCheck) {
						this.downloadContractServer(this.selectedContract)
					}

					if (this.selectedContract.contractTemplate.contractSequence.type === ContractSequenceType.TOUR_OPERATOR) {
						await this.triggerTourOperatorOnboarding()
					}
				}
				catch (error) {
					console.error(error)
					this.showError(error.message)
				}
			}, 'signContract')
		},
		setViewTerminatedContracts() {
			this.viewTerminatedContracts = true
			this.selectedContract = null
			this.contractorForContract = null
		},
		setActiveLink(id) {
			this.viewTerminatedContracts = false
			const foundContract = this.contracts.find(x => x.id === id)
			if (foundContract) {
				this.selectedContract = JSON.parse(JSON.stringify(foundContract))
				// On contract selection change set serviceLocale to user's preferred locale (if available), else English, else first available
				this.serviceLocale = this.getPreferredContractLocale(foundContract)
				// Set the contractor based on the selected contract
				this.contractorForContract = this.findContractOwner(id)
			}
		},
		handleDownload(locale) {
			this.downloadContractServer(this.selectedContract, locale)
		},
		handleAction(event) {
			this[event.function](event.params)
		},
		setLocale(code) {
			this.serviceLocale = code
		},
		getPreferredContractLocale(contract) {
			// Determine the best locale for displaying contract text/name
			// Priority: user's selectedLocale -> English -> first available non-empty locale
			const textLocales = contract?.text ? Object.keys(contract.text).filter(k => contract.text[k]) : []

			// Check if user's selected locale is available
			if (this.selectedLocale && textLocales.includes(this.selectedLocale)) {
				return this.selectedLocale
			}
			// Fall back to English if available
			if (textLocales.includes('en')) {
				return 'en'
			}
			// Fall back to first available non-empty locale
			if (textLocales.length > 0) {
				return textLocales[0]
			}
			// Ultimate fallback to defaultLocale
			return this.defaultLocale?.code || 'de'
		},
		getContractName(contract) {
			// Get contract name using the preferred locale fallback logic
			const name = contract?.contractTemplate?.name
			if (!name) return ''

			const preferredLocale = this.getPreferredContractLocale(contract)
			if (name[preferredLocale]) {
				return name[preferredLocale]
			}
			// Fallback to first available non-empty name
			const availableLocales = Object.keys(name).filter(k => name[k])
			return availableLocales.length > 0 ? name[availableLocales[0]] : ''
		},
		async triggerTourOperatorOnboarding() {
			try {
				// Find the contractor for this contract
				const contractorId = this.findContractOwner(this.selectedContract.id)?.id
				if (!contractorId) {
					console.warn('No contractor found for contract, skipping PEAK onboarding')
					return
				}

				console.log(`Triggering PEAK Tour Operator onboarding for contractor ${contractorId}`)

				const tourOperatorExecutive = new TourOperatorExecutive(this)
				const result = await tourOperatorExecutive.onboardTourOperatorFromContract(contractorId, this.userLocale)

				if (result.success) {
					console.log('PEAK Tour Operator onboarding successful:', result)
				} else {
					console.warn('PEAK Tour Operator onboarding failed:', result.errors)
				}

			} catch (error) {
				console.error('Error triggering Tour Operator onboarding:', error)
			}
		},
	},
}
</script>

<style lang="scss">
.ContractSigningView {
	.terminated-disclosure,
	.signing-disclosure {
		 > .v-expansion-panel {
			padding: 0;
			> .v-expansion-panel-text > .v-expansion-panel-text__wrapper {
				padding: 16px;

				@media (min-width: 768px) {
					padding: 32px 64px;
				}
			}
			

			> .v-expansion-panel-title {
				height: auto;
				min-height: 84px;
				padding: 16px;
				padding-right: 64px;
				border-bottom: 1px solid rgba(0, 0, 0, 0.10);
			}
		}

		.FieldSet {

			.infoText,
			>.helpText {
				font-size: 12px;
				font-weight: 400;
				line-height: 16px;
				font-style: normal;
				letter-spacing: 0.001px;
				color: black;
			}

			>.FieldSet-label {
				font-size: 17px;
				font-weight: 500;
				line-height: 22px;
				letter-spacing: -0.071px;
				color: black;
			}

			.Field label.title {
				font-size: 15px;
				font-weight: 500;
				line-height: 20px;
				letter-spacing: -0.037px;
				color: black;
			}
		}
	}

	.terminated-disclosure {
		> .v-expansion-panel > .v-expansion-panel-title {
			padding-left: 64px;
		}
	}

	.signing-disclosure {
		.v-expansion-panel {
			&-title {
				pointer-events: none;
				&__icon {
					display: none;
				}

				>div {
					flex: 1;

					&.column {
						display: none;
					}
				}

				.header-accessory {
					display: flex;
					flex-wrap: wrap;
					gap: 16px;
					align-items: center;

					>div:first-of-type {
						padding-left: 20px;
						display: flex;
						flex-direction: column;
						flex: 1;
						min-width: 200px;

						>span:first-child {
							font-size: 20px;
							font-weight: 600;
							line-height: 25px;
							letter-spacing: -0.196px;
							
							@media (max-width: 767px) {
								font-size: 18px;
								line-height: 22px;
							}
						}

						>span:last-child {
							font-size: 12px;
							font-weight: 500;
							line-height: 16px;
							letter-spacing: 0.001px;
							color: #8F8F8F
						}
					}

					>div:last-child {
						flex: 1;
						min-width: 200px;

						.info-text {
							font-size: 12px;
							font-style: normal;
							font-weight: 400;
							line-height: 16px;
							letter-spacing: 0.001px;
						}

						.status-chip {
							display: flex;
							height: 24px;
							padding: 0px 8px;
							justify-content: center;
							align-items: center;
							gap: 8px;
							border-radius: 4px;
							font-size: 13px;
							font-weight: 400;
							line-height: 18px;
							letter-spacing: 0.001px;
							color: white;
							width: fit-content;
							margin-left: auto;
						}
					}
				}
			}
		}

		.contract-text,
		.contract-text p {
			font-size: 15px !important;
			line-height: 1.7;
			letter-spacing: -0.037px;
			color: black !important;
		}

		.v-input.v-checkbox {

			.v-label {
				padding-left: 16px;
				font-size: 15px;
				font-weight: 400;
				line-height: 20px;
				letter-spacing: -0.037px;
			}
		}

		.physical-title {
			>.FieldSet-label {
				font-size: 17px;
				font-weight: 600;
				line-height: 22px;
				letter-spacing: -0.071px;
				padding-bottom: 16px;
			}
		}

		.physical-text {
			font-size: 15px;
			font-weight: 400;
			line-height: 20px;
			letter-spacing: -0.037px;
		}
	}


	.signing-sidebar {
		.sidebar {
			height: calc(100% - 64px) !important;
			overflow-y: auto !important;
			display: flex !important;
			flex-direction: column !important;
			
			.v-navigation-drawer__content {
				display: flex !important;
				flex-direction: column !important;
				height: 100% !important;
				
				> div:first-child {
					margin-bottom: 0px !important;
					display: flex !important;
					flex-direction: column !important;
					flex: 1 !important;
					min-height: 100% !important;
				}
			}
		}
	}

	.signing-sidebar-navigation {
		height: 100%;
		display: flex !important;
		flex-direction: column !important;
		
		.v-expansion-panel-title {
			min-height: 60px !important;
			display: block !important;
			font-size: 15px;
			font-weight: 600;
			line-height: 20px;
			letter-spacing: -0.037px;
			padding: 0 28px !important;
			position: relative;

			&__icon {
				position: absolute;
				top: calc(50% + 4px);
				transform: translateY(-50%);
				left: 5px;
			}
		}

		.v-expansion-panel-text__wrapper {
			padding: 0 !important;

			#navlist {
				li {
					padding-left: 28px;
					padding-right: 28px;
					list-style-type: none;
					width: 100%;
					align-items: middle;
					font-size: 15px;
					font-style: normal;
					font-weight: 400;
					line-height: 20px;
					letter-spacing: -0.037px;
					color: black;
					height: 60px;
					align-content: center;
					cursor: pointer;
					border: none;

					&.active {
						background-color: #00aeef;
						color: white !important;
						font-weight: 600;
					}
				}
			}
		}
	}

	// for some reason this doesn't work in the scoped style
	.contract-text ul {
		padding-left: 40px;
	}
	.contract-text ol {
		padding-left: 40px;
	}
}
@media print {
	.signing-sidebar {
		display: none;
	}
	.ContractSigningView {
		padding: 0px !important;
	}
}
</style>

<style lang="scss" scoped>
.ContractSigningView {
	@media print {
		.contract-information {
			margin: 0;
			padding: 0;
		}
	}

	.signing-sidebar {
		&.mobileSidebar {
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 100;
		}

		.sidebar-title {
			font-size: 15px;
			font-weight: 500;
			line-height: 20px;
			letter-spacing: -0.037px;
		}

		.bottom-navigation {
			margin-top: auto;
			padding-bottom: 60px; /* Increased padding to avoid helper icon overlap */
			> div {
				padding-left: 28px;
				padding-right: 28px;
				width: 100%;
				font-size: 15px;
				font-style: normal;
				font-weight: 400;
				line-height: 20px;
				letter-spacing: -0.037px;
				color: black;
				height: 60px;
				display: flex;
				align-items: center;
				cursor: pointer;
				border: none;
				&.active {
					background-color: #00aeef;
					color: white !important;
					font-weight: 600;
				}
			}
		}
	}
	
	.title {
		color: var(--primary-color-dark);
		font-size: 20px;
		font-style: normal;
		font-weight: 600;
		line-height: 25px;
		letter-spacing: -0.196px;

		@media (max-width: 767px) {
			font-size: 18px;
			line-height: 22px;
		}
	}
}
</style>
